import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import Overlay from "./Overlay";
import { FaChevronDown, FaTimesCircle } from "react-icons/fa";
import theme from "../../UI/theme";
import Checkbox from "./Checkbox";
import Button from "./Button";

// Keep your own button if you already replaced this:
const DropdownButton = styled.button`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-gap: 10px;
  padding: 10px;
  border: none;
  border-radius: 2px;
  box-sizing: border-box;
  font-size: ${theme.fontSize.medium};
  align-items: center;
  width: ${props => (props.width ? `${props.width}px` : "fill-available")};
  background-color: ${theme.colors.darkSpace80};
  color: ${theme.colors.textGray};
  cursor: pointer;
  border: 1px solid transparent;
  & > svg { width: 20px; height: 20px; }
  &:focus, &:hover { border: 1px solid ${theme.colors.textGray}; }
`;

const DropdownList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  position: fixed;
  width: fill-available;
  background-color: ${theme.colors.darkSpace90};
  color: ${theme.colors.textGray};
  border-radius: 1px;
  z-index: 100;
  border: 1px solid ${theme.colors.darkSpace80};
  opacity: 0;
  transition: opacity 0.1s;
  max-height: 450px;
  overflow-y: auto;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${theme.colors.darkSpace80};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.textGray};
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${theme.colors.greenEnergy};
  }
`;

const ItemRow = styled.li`
  padding: 10px;
  cursor: pointer;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  display: grid;
  grid-template-columns: 1fr auto; /* label left, checkbox right */
  align-items: center;

  :not(:last-child) { border-bottom: 1px solid ${theme.colors.darkSpace80}; }
  &:hover { background-color: ${theme.colors.darkSpace80}; }
`;

const RowLeft = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Controls = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto;
  grid-gap: 8px;
  align-items: center;
  padding: 10px;
  border-bottom: 1px solid ${theme.colors.darkSpace80};
  position: sticky;
  top: 0;
  background-color: ${theme.colors.darkSpace90};
  z-index: 1;
`;

const DropdownMultiSelect = ({
  items,                 // [{label, key?}]
  selectedKeys,          // string[]
  onChange,              // (keys: string[]) => void
  placeholder = "Select...",
  width,
  displayMode = "auto",  // "auto" (default) or "count" - maintains backwards compatibility
  customDisplayText,     // Optional function: (selectedCount, totalCount) => string
  selectAllText = "Select all",  // Custom text for select all button
  clearAllText = "Clear"         // Custom text for clear all button
}) => {
  const [open, setOpen] = useState(false);
  const listRef = useRef();
  const buttonRef = useRef();

  const normalized = useMemo(
    () => (items ?? []).map(it => ({ key: it.key ?? it.label, label: it.label })),
    [items]
  );
  const selSet = useMemo(() => new Set(selectedKeys ?? []), [selectedKeys]);

  const label = useMemo(() => {
    // If custom display function is provided, use it
    if (customDisplayText) {
      return customDisplayText(selectedKeys?.length || 0, normalized.length);
    }
    
    if (!selectedKeys || selectedKeys.length === 0) {
      return displayMode === "count" ? "0 users selected" : placeholder;
    }
    
    if (displayMode === "count") {
      return `${selectedKeys.length} user${selectedKeys.length === 1 ? '' : 's'} selected`;
    }
    
    // Original behavior when displayMode is "auto" (default) - maintains backwards compatibility
    if (selectedKeys.length === normalized.length) return "All selected";
    if (selectedKeys.length <= 2) {
      const labels = normalized.filter(i => selSet.has(i.key)).map(i => i.label);
      return labels.join(", ");
    }
    return `${selectedKeys.length} types selected`;
  }, [placeholder, normalized, selectedKeys, selSet, displayMode, customDisplayText]);

  const updateListPosition = () => {
    const screenH = window.innerHeight;
    const btnRect = buttonRef?.current?.getBoundingClientRect();
    if (!btnRect || !listRef?.current) return;

    const maxDropdownHeight = 450; // Our desired max height
    const padding = 20; // Safe distance from screen edges
    
    const availableSpaceBelow = screenH - btnRect.bottom - padding;
    const availableSpaceAbove = btnRect.top - padding;
    
    // Position above if there's not enough space below AND more space is available above
    if (availableSpaceBelow < maxDropdownHeight && availableSpaceAbove > availableSpaceBelow) {
      // Position above the button
      const finalMaxHeight = Math.min(maxDropdownHeight, availableSpaceAbove);
      listRef.current.style.maxHeight = `${finalMaxHeight}px`;
      listRef.current.style.bottom = `${screenH - btnRect.top}px`;
      listRef.current.style.top = 'auto';
    } else {
      // Position below the button
      const finalMaxHeight = Math.min(maxDropdownHeight, availableSpaceBelow);
      listRef.current.style.maxHeight = `${finalMaxHeight}px`;
      listRef.current.style.top = `${btnRect.bottom}px`;
      listRef.current.style.bottom = 'auto';
    }
    listRef.current.style.width = `${btnRect.width}px`;
    listRef.current.style.left = `${btnRect.left}px`;
    listRef.current.style.opacity = 1;
  };

  useEffect(() => {
    if (open) updateListPosition();
  }, [open]);

  const setKeys = (nextSet) => onChange?.(Array.from(nextSet));
  const toggleKey = (key) => {
    const next = new Set(selSet);
    next.has(key) ? next.delete(key) : next.add(key);
    setKeys(next);
  };
  const selectAll = () => setKeys(new Set(normalized.map(i => i.key)));
  const clearAll = () => setKeys(new Set());

  return (
    <>
      {/* If you have your own button component, just replace this <DropdownButton> block */}
      <DropdownButton ref={buttonRef} width={width} onClick={() => setOpen(s => !s)}>
        <div title={label}>{label}</div>
        <FaChevronDown />
      </DropdownButton>

      {open && (
        <>
          <DropdownList ref={listRef}>
            <Controls>
              <div style={{opacity:0.85}}>Filter</div>
              <Button tertiary small onClick={selectAll}>{selectAllText}</Button>
              <Button secondary small onClick={clearAll}>{clearAllText}</Button>
            </Controls>

            {normalized.map((item, idx) => {
              const checked = selSet.has(item.key);
              return (
                <ItemRow
                  key={item.key ?? `${item.label}-${idx}`}
                  onClick={(e) => {
                    // clicking the row toggles
                    e.stopPropagation();
                    toggleKey(item.key);
                  }}
                >
                  <RowLeft title={item.label}>{item.label}</RowLeft>

                  {/* Right-side checkbox (controlled). 
                      Stop propagation so clicking the box doesn't close the list twice. */}
                  <div onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={checked}
                      onChange={() => toggleKey(item.key)}
                      // Optional visual tweaks:
                      backgroundColor={theme.colors.darkSpace80}
                      checkedBackgroundColor={theme.colors.darkSpace80}
                      hideIcon={false}
                    />
                  </div>
                </ItemRow>
              );
            })}
          </DropdownList>
          <Overlay onClick={() => setOpen(false)} />
        </>
      )}
    </>
  );
};

export default DropdownMultiSelect;
